<template>
  <div class="container mx-auto p-4">
    <AlertLayout title="Reminders!"
      message="Kindly note that only blood units registered on Damu-Sasa will be found by the search.<br>Non-registered units should 1st be registered."
      type="warning" class="mb-4" />
    <div class="bg-white border border-t-0 rounded-lg shadow-md p-5">
      <h2 class="text-xl font-bold mb-2">Donation Information</h2>

      <div class="bg-gray-10 p-6 rounded-lg mb-6">
        <div class="grid grid-cols-2 gap-4">
          <!-- Left Column -->
          <div class="space-y-6">
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Serial Number:</label>
              <p class="text-sm font-semibold mt-3">
                {{ donation.pint_serial }}
              </p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Donor Name:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.donor?.user?.first_name ?? '' }}
                {{ donation.donor?.user?.last_name ?? '' }}
              </p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Blood Group:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.blood_group?.description ?? 'Unknown' }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Blood Volume:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.quantity }} ml</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Type of Donation:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.donation_type }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Technology:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.technology }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Donation Date:</label>
              <p class="text-sm font-semibold mt-3">{{ new Date(donation.date_added).toLocaleString() }}</p>
            </div>
          </div>
          <!-- Right Column -->
          <div class="space-y-6">
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Donated To:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.donor?.patient ?? '' }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Blood Product:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.unit_product?.description }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Blood Origin:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.origin }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Expiry Date:</label>
              <p class="text-sm font-semibold mt-3">{{ new Date(donation.expiry_date).toLocaleString() }}</p>
            </div>
            <div class="grid grid-cols-2 items-center border-t m-2">
              <label class="text-sm font-normal mt-3">Blood Status:</label>
              <p class="text-sm font-semibold mt-3">{{ donation.status }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <h2 class="text-xl font-semibold mb-4">Screening Results</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg shadow-md">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-sm tracking-wider">Hepatitis B</th>
                <th class="px-6 py-3 text-left text-sm tracking-wider">Hepatitis C</th>
                <th class="px-6 py-3 text-left text-sm tracking-wider">HIV1&2</th>
                <th class="px-6 py-3 text-left text-sm tracking-wider">Malaria</th>
                <th class="px-6 py-3 text-left text-sm tracking-wider">VDRL</th>
                <th class="px-6 py-3 text-left text-sm tracking-wider">Blood Clot</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.hepatitis_b ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.hepatitis_c ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.hiv ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.malaria ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.vdrl ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ donation.screening_test?.blood_clot ?? '' }}</td>
              </tr>
              <tr class="bg-gray-50">
                <td v-for="test in testTypes" :key="test" class="px-6 py-4 whitespace-nowrap text-sm">
                  <div class="text-xs">
                    Done by: {{ getPersonnelInfo(test, 'done_by') }}<br>
                    Confirmed by: {{ getPersonnelInfo(test, 'confirmed_by') }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="products.length > 0" class="mb-8">
        <h2 class="text-xl font-semibold mb-4">Blood Components</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg shadow-md">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Serial Number
                </th>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Blood Group</th>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Blood Product</th>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Volume</th>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Expiry Date</th>
                <th class="px-6 py-3 text-left text-xs tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(product, index) in products" :key="index" class="border-b">
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ product.pint_serial ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ product.blood_group?.description ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ product.unit_product?.name ?? '' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ product.volume ?? '' }} ml</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  {{ (new Date(product.expiry_date).toLocaleDateString()) ?? '' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ product.status ?? '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex justify-start space-x-2">
        <button class="px-4 py-2 border rounded text-orange-500 border-orange-500 mr-2"
          @click="handleBackToUnitDetails">
          Back
        </button>
        <button class="px-4 py-2 border rounded text-orange-500 border-orange-500"
          @click="downloadDonorInfo">
          Preview & Download Info
        </button>
      </div>
    </div>

    <div v-if="showPdfPreview" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="w-11/12 max-w-5xl p-6 bg-white rounded-lg shadow-lg">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold">Donation Info PDF Preview</h2>
          <button @click="closePdfPreview" class="text-gray-500 hover:text-gray-700" aria-label="Close preview">✕</button>
        </div>
        <iframe :src="pdfPreviewUrl" class="w-full h-[70vh] border rounded-md" title="Donation Info PDF Preview"></iframe>
        <div class="flex justify-end gap-3 mt-4">
          <button @click="closePdfPreview" class="px-4 py-2 border rounded text-gray-700">Cancel</button>
          <button @click="saveDonorInfoPdf" class="px-4 py-2 border rounded text-white bg-orange-500">Download PDF</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlertLayout from '@/components/AlertLayout.vue';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import api from '@/services/api';
import stFrancisLogo from '@/assets/images/St_Francis_Logo.svg';

export default {
  name: 'UnitDetailsPage',
  components: {
    AlertLayout,
  },
  data() {
    return {
      donation: [],
      products: [],
      blood_donation_id: '',
      totalVolume: 0,
      error: null,
      showDeferralPopup: false,
      showPdfPreview: false,
      pdfPreviewUrl: null,
      donorInfoPdf: null,
      testTypes: [
        'hepatitis_b',
        'hepatitis_c',
        'hiv',
        'malaria',
        'vdrl',
        'blood_clot'
      ]
    };
  },
  created() {
    this.blood_donation_id = this.$route.params.blood_donation_id;
    this.getBlooUnitDonation();
    this.getBloodProducts();
  },

  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },
    handleBackToUnitDetails() {
      this.$router.push({ name: 'InventoryManagementPage' });
    },
    async getBloodProducts() {
      try {
        const response = await api.fetchProductOnDonation(this.blood_donation_id);
        this.products = response.data || response;
        this.totalVolume = this.products.reduce((sum, product) => {
          return sum + product.volume;
        }, 0);
      } catch (error) {
        this.error = error.message || 'Failed to fetch donation';
      }
    },
    async getBlooUnitDonation() {
      try {
        const response = await api.getBloodDonation(this.blood_donation_id);
        this.donation = response.data || response;
      } catch (error) {
        this.error = error.message || 'Failed to fetch donation';
      }
    },
    getPersonnelInfo(testType, role) {
      const log = this.donation.screening_logs?.find(log => log.property === testType)
      if (!log || !log[role]) return ''
      return `${log[role].first_name} ${log[role].last_name}`
    },
    async downloadDonorInfo() {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      // jsPDF embeds raster images reliably, so convert the SVG logo to PNG first.
      const logo = new Image();
      logo.src = stFrancisLogo;
      await logo.decode();
      const logoCanvas = document.createElement('canvas');
      logoCanvas.width = logo.naturalWidth;
      logoCanvas.height = logo.naturalHeight;
      logoCanvas.getContext('2d').drawImage(logo, 0, 0);
      doc.addImage(logoCanvas.toDataURL('image/png'), 'PNG', (pageWidth - 35) / 2, 5, 35, 15);

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Blood Donation Unit Details", pageWidth / 2, 30, { align: "center" });

      // Donation Information section
      doc.setFontSize(12);
      doc.text("Donation Information", 15, 45);

      const infoColumns = [
        { header: "Field", dataKey: "field" },
        { header: "Value", dataKey: "value" },
      ];

      const donorName = `${this.donation.donor?.user?.first_name ?? ''} ${this.donation.donor?.user?.last_name ?? ''}`.trim();
      const bloodGroup = this.donation.blood_group?.description ?? 'Unknown';
      const dateDonated = new Date(this.donation.date_added).toLocaleString();
      const pintSerial = this.donation.pint_serial ?? 'Unknown';

      const infoData = [
        { field: "Serial Number", value: pintSerial },
        { field: "Donor Name", value: donorName },
        { field: "Blood Group", value: bloodGroup },
        { field: "Blood Volume", value: `${this.donation.quantity} ml` },
        { field: "Type of Donation", value: this.donation.donation_type },
        { field: "Technology", value: this.donation.technology },
        { field: "Donation Date", value: dateDonated },
        { field: "Donated To", value: this.donation.donor?.patient ?? '' },
        { field: "Blood Product", value: this.donation.unit_product?.description },
        { field: "Blood Origin", value: this.donation.origin },
        { field: "Expiry Date", value: new Date(this.donation.expiry_date).toLocaleString() },
        { field: "Blood Status", value: this.donation.status },
      ];

      doc.autoTable({
        startY: 50,
        columns: infoColumns,
        body: infoData,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: "bold" },
        columnStyles: { field: { cellWidth: 40 }, value: { cellWidth: 120 } },
      });

      // Screening Results section
      const screeningY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(12);
      doc.text("Screening Results", 15, screeningY);

      const screeningColumns = [
        { header: "Test", dataKey: "test" },
        { header: "Result", dataKey: "result" },
        { header: "Done By", dataKey: "done_by" },
        { header: "Confirmed By", dataKey: "confirmed_by" },
      ];

      const screeningLabels = {
        hepatitis_b: "Hepatitis B",
        hepatitis_c: "Hepatitis C",
        hiv: "HIV1&2",
        malaria: "Malaria",
        vdrl: "VDRL",
        blood_clot: "Blood Clot",
      };

      const screeningData = this.testTypes.map((test) => ({
        test: screeningLabels[test] ?? test,
        result: this.donation.screening_test?.[test] ?? '',
        done_by: this.getPersonnelInfo(test, 'done_by') || '',
        confirmed_by: this.getPersonnelInfo(test, 'confirmed_by') || '',
      }));

      doc.autoTable({
        startY: screeningY + 5,
        columns: screeningColumns,
        body: screeningData,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: "bold" },
        columnStyles: {
          test: { cellWidth: 35 },
          result: { cellWidth: 35 },
          done_by: { cellWidth: 45 },
          confirmed_by: { cellWidth: 45 },
        },
      });

      this.donorInfoPdf = doc;
      this.pdfPreviewUrl = URL.createObjectURL(doc.output('blob'));
      this.showPdfPreview = true;
    },
    saveDonorInfoPdf() {
      if (this.donorInfoPdf) {
        const pintSerial = this.donation.pint_serial ?? 'Unknown';
        this.donorInfoPdf.save(`screening-info-${pintSerial}.pdf`);
      }
      this.closePdfPreview();
    },
    closePdfPreview() {
      this.showPdfPreview = false;
      if (this.pdfPreviewUrl) URL.revokeObjectURL(this.pdfPreviewUrl);
      this.pdfPreviewUrl = null;
      this.donorInfoPdf = null;
    }
  },
};
</script>
